import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('adminToken')}`
});

const CronMonitoring = () => {
  const [parserLogs, setParserLogs] = useState('');
  const [agentLogs, setAgentLogs] = useState('');
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [activeTab, setActiveTab] = useState('scheduler');
  const [runningJob, setRunningJob] = useState(null);

  useEffect(() => {
    loadData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [schedulerRes, parserLogsRes, agentLogsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/scheduler/status`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/cron-logs`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/scheduler/logs`, { headers: getAuthHeaders() })
      ]);
      setSchedulerStatus(schedulerRes.data);
      setParserLogs(parserLogsRes.data.logs);
      setAgentLogs(agentLogsRes.data.logs);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const runJob = async (jobId, jobName) => {
    setRunningJob(jobId);
    setNotification({ message: `Запуск ${jobName}...`, type: 'info' });
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/scheduler/run-job/${jobId}`,
        {},
        { headers: getAuthHeaders(), timeout: 600000 }
      );
      const result = response.data.result || {};
      setNotification({
        message: `✅ ${jobName} завершен! ${
          result.articles_generated !== undefined 
            ? `Статей создано: ${result.articles_generated}` 
            : result.articles_created !== undefined 
              ? `Статей создано: ${result.articles_created}` 
              : ''
        }`,
        type: 'success'
      });
      setTimeout(loadData, 2000);
    } catch (error) {
      setNotification({ 
        message: `❌ Ошибка: ${error.response?.data?.detail || error.message}`, 
        type: 'error' 
      });
    } finally {
      setRunningJob(null);
    }
  };

  const clearLogs = async () => {
    if (!window.confirm('Очистить логи парсера?')) return;
    try {
      await axios.post(`${API_URL}/api/admin/cron-logs/clear`, {}, {
        headers: getAuthHeaders()
      });
      setNotification({ message: 'Логи очищены', type: 'success' });
      loadData();
    } catch (error) {
      setNotification({ message: 'Ошибка очистки логов', type: 'error' });
    }
  };

  const formatNextRun = (dateStr) => {
    if (!dateStr) return 'Не запланировано';
    const date = new Date(dateStr);
    return date.toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  if (loading) {
    return <div className="p-6 text-center">Загрузка...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification && (
        <div className={`p-4 rounded-lg ${
          notification.type === 'error' ? 'bg-red-50 text-red-800' :
          notification.type === 'success' ? 'bg-green-50 text-green-800' :
          'bg-blue-50 text-blue-800'
        }`}>
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Планировщик задач</h1>
          <p className="text-gray-600 mt-1">Автоматический парсинг новостей и генерация статей AI</p>
        </div>
        <button
          onClick={loadData}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-medium transition"
        >
          🔄 Обновить
        </button>
      </div>

      {/* Scheduler Status */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">📅 Статус планировщика</h2>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            schedulerStatus?.status === 'running' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {schedulerStatus?.status === 'running' ? '🟢 Активен' : '🔴 Остановлен'}
          </span>
        </div>

        {/* Jobs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {schedulerStatus?.jobs?.map((job) => (
            <div key={job.id} className="border rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-gray-900">
                    {job.id === 'news_parser' ? '📰 Парсер новостей' : '🤖 AI-агент'}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">{job.name}</p>
                </div>
                <button
                  onClick={() => runJob(job.id, job.name)}
                  disabled={runningJob === job.id}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition ${
                    runningJob === job.id
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : job.id === 'ai_agent'
                        ? 'bg-purple-500 hover:bg-purple-600 text-white'
                        : 'bg-orange-500 hover:bg-orange-600 text-white'
                  }`}
                >
                  {runningJob === job.id ? '⏳ Выполняется...' : '▶️ Запустить'}
                </button>
              </div>
              <div className="mt-3 space-y-1">
                <div className="text-xs text-gray-500">
                  <span className="font-medium">Расписание:</span> {job.trigger}
                </div>
                <div className="text-xs text-gray-500">
                  <span className="font-medium">Следующий запуск:</span> {formatNextRun(job.next_run)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('scheduler')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'scheduler'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📋 Логи парсера
          </button>
          <button
            onClick={() => setActiveTab('agent')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'agent'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            🤖 Логи AI-агента
          </button>
        </nav>
      </div>

      {/* Logs */}
      <div className="bg-gray-900 rounded-lg p-4">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-white font-medium">
            {activeTab === 'scheduler' ? '📋 Логи парсера новостей' : '🤖 Логи AI-агента'}
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-gray-400 text-xs">Автообновление каждые 30 сек</span>
            {activeTab === 'scheduler' && (
              <button
                onClick={clearLogs}
                className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs font-medium transition"
              >
                🗑️ Очистить
              </button>
            )}
          </div>
        </div>
        <pre className="text-green-400 font-mono text-sm overflow-x-auto whitespace-pre-wrap max-h-96 overflow-y-auto">
          {activeTab === 'scheduler' 
            ? (parserLogs || 'Логи пусты. Запустите парсинг для просмотра результатов.')
            : (agentLogs || 'Логи AI-агента пусты. Запустите агент для просмотра результатов.')
          }
        </pre>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-sm text-blue-600 font-medium">📰 Парсер новостей</div>
          <div className="text-2xl font-bold text-blue-900 mt-1">9:00</div>
          <div className="text-xs text-blue-600 mt-1">Ежедневно (Moscow Time)</div>
          <p className="text-xs text-blue-700 mt-2">Собирает новости с настроенных источников</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="text-sm text-purple-600 font-medium">🤖 AI-агент</div>
          <div className="text-2xl font-bold text-purple-900 mt-1">10:00</div>
          <div className="text-xs text-purple-600 mt-1">Ежедневно (Moscow Time)</div>
          <p className="text-xs text-purple-700 mt-2">Генерирует статьи с помощью Claude AI</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-sm text-green-600 font-medium">⚙️ Технология</div>
          <div className="text-lg font-bold text-green-900 mt-1">APScheduler</div>
          <div className="text-xs text-green-600 mt-1">AsyncIO Backend</div>
          <p className="text-xs text-green-700 mt-2">Встроен в FastAPI сервер</p>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 p-6 rounded-lg">
        <h3 className="font-bold text-blue-900 mb-3">ℹ️ Как это работает:</h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li>• <strong>Парсер новостей (9:00):</strong> Собирает актуальные новости с настроенных источников (RSS/HTML)</li>
          <li>• <strong>AI-агент (10:00):</strong> Ищет информацию в Google и генерирует уникальные статьи с помощью Claude AI</li>
          <li>• <strong>Вручную:</strong> Вы можете запустить любую задачу в любое время кнопкой «Запустить»</li>
          <li>• <strong>Настройки AI:</strong> Управляйте поисковыми запросами на странице «AI-агент»</li>
          <li>• <strong>Источники:</strong> Управляйте источниками новостей на странице «Источники новостей»</li>
        </ul>
      </div>
    </div>
  );
};

export default CronMonitoring;
